import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'

import Link from '@components/common/Link/Link'
import Module from '@components/modules'
import DeliveredStatus from '@components/ui/Icons/DeliveredStatus'
import PreadviceStatus from '@components/ui/Icons/PreadviceStatus'
import ShippedStatus from '@components/ui/Icons/ShippedStatus'
import Media from '@components/ui/Media/Media'
import { refineProductTitle } from '@components/views/Product/helpersv2'
import { useCustomerOrder } from '@features/account/api/get-customer-order'
import { formatPriceV2 } from '@lib/format-price'
import { ShipmentState } from '@lib/graphql/graphql'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { generateMediaUrlV2 } from '@lib/utils'
import { selectCountry } from '@redux/globalSlice'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import s from './M60OrderView.module.scss'

type M60OrderViewProps = {
  orderId
  orderNotFoundContent: any
}

const M60OrderView: FC<M60OrderViewProps> = ({ orderId, ...props }) => {
  const { data, isLoading, isFetching } = useCustomerOrder({ orderId })
  const country = useSelector(selectCountry)

  const order = data?.order

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isFetching) {
      const timer = setTimeout(() => {
        setLoading(false)
      }, 1000)

      return () => clearTimeout(timer)
    } else {
      setLoading(true)
    }
  }, [isFetching])

  if (!order && !loading) {
    return (
      props.orderNotFoundContent &&
      props.orderNotFoundContent.length > 0 &&
      props.orderNotFoundContent.map((component, index) => {
        return (
          <Module
            {...component}
            __typename={
              component.contentTypeAlias.charAt(0).toUpperCase() +
              component.contentTypeAlias.slice(1)
            }
            key={index}
            lcp={
              props.orderNotFoundContent
                .filter((x) => x.__typename !== 'Spacer')
                .findIndex((x) => x === component) < 2
            }
          />
        )
      })
    )
  }

  if (loading) return <SkeletonOrder />

  const lineItems = mapEdgesToItems(order.lineItems)

  const totalDiscount = lineItems.reduce(
    (acc, item) =>
      acc +
      mapEdgesToItems(item.discountApplications).reduce(
        (sum, app) => sum + app.discountedAmount.centAmount,
        0,
      ),
    0,
  )

  const taxPercent = Number(
    Number(
      (order.taxedPrice.gross.centAmount / order.taxedPrice.net.centAmount -
        1) *
        100,
    ).toFixed(2),
  )

  const taxAmount =
    order.taxedPrice.gross.centAmount - order.taxedPrice.net.centAmount

  return (
    <div className={s.root}>
      {/* <ShipmentTimeline shipmentState={order.shipmentState} /> */}

      <div className={clsx(s.section, s['col-4'])}>
        <div className={s.blockGroup}>
          <div className={s.block}>
            <div className={s.block__title}>Date</div>
            {new Date(order.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
          {order.externalReference && (
            <div className={s.block}>
              <div className={s.block__title}>Order Number</div>
              {order.externalReference}
            </div>
          )}
        </div>
        <div className={s.blockGroup}>
          {/* {order.trackingDetails.length > 0 && (
            <>
              <div className={s.block}>
                <div className={s.block__title}>
                  {order.trackingDetails.length > 1 ? 'Carriers' : 'Carrier'}
                </div>
                {trackingDetailsMap(order.trackingDetails)
                  .map((trackingDetail) => trackingDetail.carrier)
                  .join(', ')}
              </div>
              <div className={s.block}>
                <div className={s.block__title}>
                  {order.trackingDetails.length > 1
                    ? 'Tracking Numbers'
                    : 'Tracking Number'}
                </div>
                {trackingDetailsMap(order.trackingDetails)
                  .map((trackingDetail) => (
                    <Link
                      className={s.link}
                      link={{ url: trackingDetail.trackingUrl, target: '_new' }}
                    >
                      {trackingDetail.trackingNumber}
                    </Link>
                  ))
                  .join(', ')}
              </div>
            </>
          )} */}
        </div>
        <div className={s.block}>
          <div className={s.block__title}>Billing Address</div>
          <TrustedHtml
            html={order.billingAddress.formatted.replaceAll('\n', '<br />')}
          />
        </div>
        <div className={s.block}>
          <div className={s.block__title}>Shipping Address</div>
          <TrustedHtml
            html={order.shippingAddress.formatted.replaceAll('\n', '<br />')}
          />
        </div>
      </div>

      <div className={clsx(s.section, s['col-1'])}>
        <div className={s.item__list__header}>
          <div className={s.item__title}>Product</div>
          <div className={s.item__quantity}>Qty</div>
          <div className={s.item__price}>Unit Price</div>
        </div>
        {lineItems.map((item) => {
          const originalSubtotal =
            item.quantity * item.unitPrice.value.centAmount

          const subtotal =
            item.quantity *
            (item.unitPrice.discountedPrice
              ? item.unitPrice.discountedPrice.value.centAmount
              : item.unitPrice.value.centAmount)

          const totalDiscountLineItem = mapEdgesToItems(
            item.discountApplications,
          ).reduce((sum, app) => sum + app.discountedAmount.centAmount, 0)

          return (
            <div key={item.id} className={s.item}>
              <div className={s.img}>
                {item.variant?.image.src ? (
                  <Media
                    mediaWithCrops={
                      {
                        url: generateMediaUrlV2(item.variant.image?.src),
                        media: {
                          __typename: 'Image',
                          url: generateMediaUrlV2(item.variant.image?.src),
                        },
                      } as MediaWithCropsFragment
                    }
                    mediaClassName={s.image}
                  />
                ) : (
                  <div className={s.noImage}>{item.variantName}</div>
                )}
              </div>
              <div className={s.item__title}>
                <Link
                  className={s.link}
                  link={
                    item.variant
                      ? {
                          url: `/products/${item.productSlug}`,
                          target: '_new',
                        }
                      : null
                  }
                  query={item.variant ? { itemNumber: item.sku } : null}
                >
                  <span className={s.underlineAnimation}>
                    {item.productName}
                  </span>
                  <div className={s.item__subtitles}>
                    <p>{refineProductTitle(item.variantName).secondaryName}</p>
                    <p>Item Number: {item.sku}</p>
                  </div>
                  <div className={clsx(s.item__subtitles, s.mobileOnly)}>
                    <p>
                      Qty: {item.quantity} x{' '}
                      <span>
                        {formatPriceV2(country.code, item.unitPrice.value, 2)}
                      </span>
                      {totalDiscountLineItem > 0 && (
                        <div className={s.discount}>
                          {' '}
                          {formatPriceV2(
                            country.code,
                            item.unitPrice.discountedPrice?.value ??
                              item.unitPrice.value,
                            2,
                          )}
                        </div>
                      )}
                    </p>
                  </div>
                </Link>
              </div>

              <div className={s.item__quantity}>{item.quantity}</div>
              <div className={s.item__price}>
                <span>
                  {formatPriceV2(
                    country.code,
                    item.unitPrice.discountedPrice?.value ??
                      item.unitPrice.value,
                    2,
                  )}
                </span>
                {totalDiscountLineItem > 0 && (
                  <div className={s.discount}>
                    {' '}
                    {formatPriceV2(
                      country.code,
                      {
                        centAmount:
                          (
                            item.unitPrice.discountedPrice?.value ??
                            item.unitPrice.value
                          ).centAmount - totalDiscount,
                        currencyCode: item.total.currencyCode,
                        fractionDigits: item.total.fractionDigits,
                      },
                      2,
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className={clsx(s.section, s.short__section)}>
        <div className={clsx(s.block, s.block__row)}>
          <div>Subtotal</div>
          <div>
            {formatPriceV2(
              country.code,
              {
                centAmount: order.subtotal.centAmount,
                currencyCode: order.subtotal.currencyCode,
                fractionDigits: order.subtotal.fractionDigits,
              },
              2,
            )}
          </div>
        </div>
        <div className={clsx(s.block, s.block__row)}>
          <div>Delivery</div>
          <div>
            {order.shippingLines.find(Boolean)?.subtotal.centAmount > 0
              ? formatPriceV2(
                  country.code,
                  order.shippingLines.find(Boolean)?.subtotal,
                  2,
                )
              : 'Free'}
          </div>
        </div>
        {taxAmount > 0 && (
          <div className={clsx(s.block, s.block__row)}>
            <div>
              {order.shippingAddress.countryCode == 'US' ? 'Tax' : 'VAT'} (
              {taxPercent}%)
            </div>
            <div>
              {formatPriceV2(
                country.code,
                {
                  centAmount: taxAmount,
                  currencyCode: order.taxedPrice.gross.currencyCode,
                  fractionDigits: order.taxedPrice.gross.fractionDigits,
                },
                2,
              )}
            </div>
          </div>
        )}
        {totalDiscount > 0 && (
          <div className={clsx(s.block, s.block__row)}>
            <div>Discount</div>
            <div className={s.discount}>
              {formatPriceV2(
                country.code,
                {
                  centAmount: -1 * totalDiscount,
                  currencyCode: order.total.currencyCode,
                  fractionDigits: order.total.fractionDigits,
                },
                2,
              )}
            </div>
          </div>
        )}
        <div className={clsx(s.block, s.block__row, s.bold)}>
          <div>Total</div>
          <div>
            {formatPriceV2(
              country.code,
              {
                centAmount: order.total.centAmount,
                currencyCode: order.total.currencyCode,
                fractionDigits: order.total.fractionDigits,
              },
              2,
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default M60OrderView

const SkeletonOrder: FC = () => {
  return (
    <div className={s.root}>
      {/* <ShipmentTimeline loading shipmentState={ShipmentState.Pending} /> */}

      <div className={clsx(s.section, s['col-4'])}>
        <div className={s.blockGroup}>
          <div className={s.block}>
            <div className={s.block__title}>Date</div>
            <div
              className={s.skeleton}
              style={{ width: '100%', height: '20px' }}
            />
          </div>
        </div>
        <div className={s.blockGroup}></div>
        <div className={s.block}>
          <div className={s.block__title}>Billing Address</div>
          <div
            className={s.skeleton}
            style={{ width: '100%', height: '100px' }}
          />
        </div>
        <div className={s.block}>
          <div className={s.block__title}>Shipping Address</div>
          <div
            className={s.skeleton}
            style={{ width: '100%', height: '100px' }}
          />
        </div>
      </div>

      <div className={clsx(s.section, s['col-1'])}>
        <div className={s.item__list__header}>
          <div className={s.item__title}>Product</div>
          <div className={s.item__quantity}>Qty</div>
          <div className={s.item__price}>Unit Price</div>
        </div>
        <SkeletonItem />
        <SkeletonItem />
        <SkeletonItem />
      </div>

      <div className={clsx(s.section, s.short__section)}>
        <div className={clsx(s.block, s.block__row)}>
          <div
            className={s.skeleton}
            style={{ width: '120px', height: '20px' }}
          />
          <div
            className={s.skeleton}
            style={{ width: '80px', height: '20px' }}
          />
        </div>
        <div className={clsx(s.block, s.block__row)}>
          <div
            className={s.skeleton}
            style={{ width: '120px', height: '20px' }}
          />
          <div
            className={s.skeleton}
            style={{ width: '80px', height: '20px' }}
          />
        </div>
        <div className={clsx(s.block, s.block__row)}>
          <div
            className={s.skeleton}
            style={{ width: '120px', height: '20px' }}
          />
          <div
            className={s.skeleton}
            style={{ width: '80px', height: '20px' }}
          />
        </div>
        <div className={clsx(s.block, s.block__row)}>
          <div
            className={s.skeleton}
            style={{ width: '120px', height: '20px' }}
          />
          <div
            className={s.skeleton}
            style={{ width: '80px', height: '20px' }}
          />
        </div>
      </div>
    </div>
  )
}

const SkeletonItem: FC = () => {
  return (
    <div className={s.item}>
      {/* Skeleton for Image */}
      <div className={s.img}>
        <div
          className={s.skeleton}
          style={{ width: '115px', height: '115px', borderRadius: '6px' }}
        />
      </div>

      {/* Skeleton for Title and Details */}
      <div className={s.item__title}>
        <div
          className={s.skeleton}
          style={{ width: '60%', height: '20px', marginBottom: '8px' }}
        />
        <div className={s.item__subtitles}>
          <div
            className={s.skeleton}
            style={{ width: '40%', height: '20px', marginBottom: '4px' }}
          />
          <div
            className={s.skeleton}
            style={{ width: '30%', height: '20px' }}
          />
        </div>
        <div className={clsx(s.item__subtitles, s.mobileOnly)}>
          <div
            className={s.skeleton}
            style={{ width: '50%', height: '20px', marginTop: '8px' }}
          />
        </div>
      </div>

      {/* Skeleton for Quantity */}
      <div className={s.item__quantity}>
        <div className={s.skeleton} style={{ width: '30px', height: '20px' }} />
      </div>

      {/* Skeleton for Price */}
      <div className={s.item__price}>
        <div className={s.skeleton} style={{ width: '60px', height: '20px' }} />
      </div>
    </div>
  )
}

const ShipmentTimeline: FC<{
  loading?: boolean
  shipmentState: ShipmentState
}> = ({ loading, shipmentState }) => {
  let shipmentStateNo = 0 // pending
  if (shipmentState == ShipmentState.Delivered) {
    shipmentStateNo = 3
  }
  if (shipmentState == ShipmentState.Shipped) {
    shipmentStateNo = 2
  }
  if (shipmentState == ShipmentState.Ready) {
    shipmentStateNo = 1
  }

  const shippingStates: Array<{
    mobileName: string
    desktopName: string
    icon: JSX.Element
    reach: 'full' | 'half' | 'empty'
    first: boolean
    last: boolean
  }> = [
    {
      first: true,
      last: false,
      mobileName: 'Preadvice',
      desktopName: 'Preadvice',
      icon: (
        <PreadviceStatus
          color={'var(--color)'}
          fill={'#f2f0eb'}
          className={s.timelineSvg}
        />
      ),
      reach: shipmentStateNo >= 1 ? 'full' : 'empty',
    },
    {
      first: false,
      last: false,
      mobileName: 'In transit',
      desktopName: 'Your package is in transit',
      icon: (
        <ShippedStatus
          color={'var(--color)'}
          fill={'#f2f0eb'}
          className={s.timelineSvg}
        />
      ),
      reach:
        shipmentStateNo >= 2 ? 'full' : shipmentStateNo >= 1 ? 'half' : 'empty',
    },
    {
      first: false,
      last: true,
      mobileName: 'Delivered',
      desktopName: 'Your order has been delivered',
      icon: (
        <DeliveredStatus
          color={'var(--color)'}
          fill={'#f2f0eb'}
          className={s.timelineSvg}
        />
      ),
      reach:
        shipmentStateNo >= 3 ? 'full' : shipmentStateNo >= 2 ? 'half' : 'empty',
    },
  ]

  return (
    <div
      className={clsx(
        { [s.loading]: loading },
        s.section,
        s.shippingTimeline,
        s['col-3'],
      )}
    >
      {shippingStates.map((state, stateIdx) => (
        <div key={state.desktopName} className={s.timelineItem}>
          <div
            className={clsx(s.timelineText, {
              [s.timelineTextGrey]:
                state.reach === 'empty' || state.reach === 'half',
            })}
          >
            <div className={s.tabletText}>{state.mobileName}</div>
            <div className={s.desktopText}>{state.desktopName}</div>
          </div>
          <div className={s.timelineGraphics}>
            {state.first ? (
              <div className={s.timelineFirst}></div>
            ) : (
              <>
                {(state.reach === 'full' || state.reach == 'empty') && (
                  <div
                    className={clsx(s.timelineLine, {
                      [s.timelineGrey]: state.reach === 'empty',
                    })}
                  ></div>
                )}
                {state.reach === 'half' && (
                  <>
                    <div className={s.timelineDot}></div>
                    <div className={s.timelineDotLine}></div>
                  </>
                )}
              </>
            )}

            <div
              className={clsx(s.timelineIcon, {
                [s.timelineIconGrey]:
                  state.reach === 'empty' || state.reach === 'half',
              })}
            >
              {state.icon}
            </div>

            {!state.last && (
              <div
                className={clsx(s.timelineLine, {
                  [s.timelineGrey]:
                    state.reach === 'empty' ||
                    (!state.last &&
                      shippingStates[stateIdx + 1].reach === 'empty'),
                })}
              ></div>
            )}
          </div>
        </div>
      ))}

      {/* <div className={s.shippingTimelineTitle}>
        {shipmentState == ShipmentState.Delivered
          ? 'Your order has been delivered'
          : null}
        {shipmentState == ShipmentState.Ready
          ? 'Your order is about to be shipped.'
          : null}
        {shipmentState == ShipmentState.Shipped
          ? 'Your order is on its way'
          : null}
      </div> */}
    </div>
  )
}

const trackingDetailsMap = (trackingDetails) => {
  // remap tracking details to a map, if trackingNumber contains "FRWD AIR" then use "Forward Air" as the carrier.
  // Tracking URL, should be prepended by "https://www.forwardair.com/tracking?numbers="
  // The Tracking Number can be found by regex "FRWD AIR: ([0-9]+)"

  return trackingDetails.map((trackingDetail) => {
    let carrier = trackingDetail.carrier
    let trackingNumber = trackingDetail.trackingNumber
    let trackingUrl = trackingDetail.trackingUrl

    if (trackingNumber.includes('FRWD AIR') && trackingNumber.includes('UPS')) {
      carrier = 'Forward Air'
      trackingNumber = trackingNumber.match(/FRWD AIR: ([0-9]+)/)[1]
      trackingUrl = `https://www.forwardair.com/tracking?numbers=${trackingNumber}`
    }

    return {
      carrier,
      trackingNumber,
      trackingUrl,
    }
  })
}
